import { clsx } from '@alexis/helpers/clsx';
import { countriesConfig } from '@alexis/helpers/country';
import React, { forwardRef, useCallback, useEffect, useMemo, useState } from 'react';
import { AutoSizer, List, type ListRowProps, type Size } from 'react-virtualized';

import type { Country } from '@wegoTypes/country';
import type { CurrentSite } from '@wegoTypes/currentSite';
import { Translations, TranslationsKey } from '@wegoTypes/translations';

import CircleTickIcon from '../icons/circle_tick.svg';
import WarningIcon from '../icons/warning.svg';
import styles from '../styles/components/CountrySelector.module.scss';

interface CountrySelectorProps {
  className?: string;
  currentSite: CurrentSite;
  hasError?: boolean;
  id?: string;
  isRtl: boolean;
  isValidated: boolean;
  message?: string;
  name?: string;
  onCountrySelected(country: Country): void;
  placeholder: string;
  selectedCountry: Country | null | undefined;
  style?: React.CSSProperties;
  translations: Translations;
}

const CountrySelector = forwardRef<HTMLInputElement, CountrySelectorProps>(
  (
    {
      className,
      currentSite,
      hasError,
      id,
      isRtl,
      isValidated,
      message,
      name,
      onCountrySelected,
      placeholder,
      selectedCountry,
      style,
      translations,
    },
    ref,
  ): JSX.Element => {
    const [isFocus, setIsFocus] = useState<boolean>(false);

    const [searchQuery, setSearchQuery] = useState<string>('');

    const countries = useMemo<Array<Country>>(() => {
      const currentSiteCountry = countriesConfig.splice(
        countriesConfig.findIndex((country) => country.code === currentSite.countryCode),
        1,
      );

      countriesConfig.unshift(currentSiteCountry[0]);

      return countriesConfig;
    }, [currentSite]);

    const filterCountries = useMemo<Array<Country>>(() => {
      if (!searchQuery) {
        return countries;
      } else {
        const lowerCaseSearchQuery = searchQuery.toLowerCase().replace(/\+/g, '').trim();

        return countries.filter((country) => {
          const countryName = country.name.toLowerCase();
          const countryCode = country.code.toLowerCase();
          const countryNameLocale = (
            translations[`pos_${countryCode}` as TranslationsKey] as string
          ).toLowerCase();
          const callingCode = country.callingCode.toString();

          return (
            countryName.startsWith(lowerCaseSearchQuery) ||
            countryCode.startsWith(lowerCaseSearchQuery) ||
            countryNameLocale.startsWith(lowerCaseSearchQuery) ||
            callingCode.startsWith(lowerCaseSearchQuery)
          );
        });
      }
    }, [countries, searchQuery, translations]);

    // Set is focus to false if body is clicked
    useEffect(() => {
      if (isFocus) {
        const handleBodyOnClick = (): void => {
          setIsFocus(false);
        };

        const timerId = setTimeout(() => {
          document.body.addEventListener('click', handleBodyOnClick);
        }, 0);

        return () => {
          clearTimeout(timerId);
          document.body.removeEventListener('click', handleBodyOnClick);
        };
      }
    }, [isFocus]);

    useEffect(() => {
      // Clear field when the value is empty
      if (isFocus || !selectedCountry) {
        setSearchQuery('');
      } else {
        if (!!selectedCountry) {
          setSearchQuery(translations[selectedCountry.label as TranslationsKey] as string);
        }
      }
    }, [isFocus, selectedCountry, translations]);

    const handleCountrySelected = useCallback<(country: Country) => void>(
      (country: Country) => {
        onCountrySelected(country);
        setIsFocus(false);
      },
      [onCountrySelected],
    );

    const handleKeyDown = useCallback<(event: React.KeyboardEvent<HTMLInputElement>) => void>(
      (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
          event.preventDefault();

          if (!!event.currentTarget.value && filterCountries.length > 0) {
            handleCountrySelected(filterCountries[0]);
          }
        }
      },
      [filterCountries, handleCountrySelected],
    );

    const rowRenderer = useCallback<({ key, index, style }: ListRowProps) => JSX.Element>(
      ({ key, index, style }: ListRowProps) => {
        const country = filterCountries[index];
        const countryCode = country.code.toLowerCase();

        return (
          <div key={key} style={style}>
            <div
              className={styles.country}
              style={isRtl ? { direction: 'rtl' } : {}}
              onClick={() => handleCountrySelected(country)}
            >
              <div className={styles.countryFlagAndNameContainer}>
                <i
                  className={clsx(
                    styles.countryFlags,
                    styles[countryCode],
                    styles.medium,
                    styles.withLightBackground,
                  )}
                ></i>
                <div className={styles.name}>
                  {translations[country.label as TranslationsKey] as string}
                </div>
              </div>

              <div className={styles.countryCode}>{countryCode}</div>
            </div>
          </div>
        );
      },
      [filterCountries, handleCountrySelected, translations],
    );

    const defaultInputId = 'country-name';

    return (
      <div
        className={clsx(
          styles.container,
          className,
          isRtl && styles.rtl,
          isFocus && styles.focus,
          isValidated && styles.validated,
          !!hasError && styles.error,
        )}
        style={style}
      >
        <div className={styles.inputContainer} onClick={(e) => e.stopPropagation()}>
          <input
            // avoid auto fill from chrome
            autoComplete='do-not-autofill'
            id={id ?? defaultInputId}
            name={name}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSearchQuery(e.target.value)}
            onFocus={() => setIsFocus(true)}
            onKeyDown={handleKeyDown}
            ref={ref}
            spellCheck={false}
            type='text'
            value={searchQuery}
          />

          <label htmlFor={id ?? defaultInputId}>{placeholder}</label>

          {isValidated && !hasError ? <CircleTickIcon className={styles.validation} /> : null}

          {isValidated && !!hasError ? <WarningIcon className={styles.validation} /> : null}

          {isFocus ? (
            <div
              className={styles.dropdown}
              style={{ height: `${Math.min(5, filterCountries.length) * 48 + 24}px` }}
            >
              <AutoSizer>
                {({ height, width }: Size) => (
                  <List
                    height={height}
                    overscanRowCount={5}
                    rowCount={filterCountries.length}
                    rowHeight={48}
                    rowRenderer={rowRenderer}
                    tabIndex={null}
                    width={width}
                  />
                )}
              </AutoSizer>
            </div>
          ) : null}

          {!(isValidated || hasError) && <i className={clsx(styles.triangle, styles.down)} />}
        </div>

        <div className={styles.message}>{message}</div>
      </div>
    );
  },
);

CountrySelector.displayName = 'CountrySelector';
export default CountrySelector;
